import { useRef, useState, useEffect, useMemo } from 'react';
import Map, { Marker, NavigationControl, Source, Layer, MapRef } from 'react-map-gl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiZGF2aWRhZmZpbmlnbyIsImEiOiJjbGtjYXdweTQwbTR1M3FtdGl1Mm5ncXE3In0.mDm0tCW7GtHNQadtA6RqXQ';

interface SavLocationMapProps {
  incidentLat: number;
  incidentLng: number;
  garantLat?: number;
  garantLng?: number;
  garantName?: string;
  height?: string;
  distanceLabelPrefix?: string;
  distanceLabelSuffix?: string;
  hideDistanceLabel?: boolean;
  onRouteLoaded?: (info: { distanceKm: number; durationMin: number }) => void;
}

interface RouteInfo {
  geometry: GeoJSON.Geometry;
  distanceKm: number;
  durationMin: number;
}

export function SavLocationMap({
  incidentLat,
  incidentLng,
  garantLat,
  garantLng,
  garantName,
  height = '256px',
  distanceLabelPrefix = 'La panne est survenue',
  distanceLabelSuffix = 'de votre point de vente',
  hideDistanceLabel = false,
  onRouteLoaded,
}: SavLocationMapProps) {
  const mapRef = useRef<MapRef>(null);
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null);
  const hasGarant = garantLat != null && garantLng != null;

  const initialViewState = useMemo(() => ({
    longitude: incidentLng,
    latitude: incidentLat,
    zoom: hasGarant ? 8 : 15,
  }), [incidentLat, incidentLng, hasGarant]);

  // Fetch route from Mapbox Directions API
  useEffect(() => {
    if (!hasGarant) return;
    let cancelled = false;

    (async () => {
      try {
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${incidentLng},${incidentLat};${garantLng},${garantLat}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;
        const res = await fetch(url);
        const data = await res.json();
        if (cancelled || !data.routes?.[0]) return;

        const route = data.routes[0];
        const next = {
          geometry: route.geometry,
          distanceKm: Math.round(route.distance / 100) / 10,
          durationMin: Math.round(route.duration / 60),
        };
        setRouteInfo(next);
        onRouteLoaded?.({ distanceKm: next.distanceKm, durationMin: next.durationMin });
      } catch (err) {
        console.warn('Mapbox Directions API error:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [incidentLat, incidentLng, garantLat, garantLng, hasGarant]);

  // Fit bounds when route is loaded
  useEffect(() => {
    if (!hasGarant || !mapRef.current) return;
    try {
      const minLng = Math.min(incidentLng, garantLng!);
      const maxLng = Math.max(incidentLng, garantLng!);
      const minLat = Math.min(incidentLat, garantLat!);
      const maxLat = Math.max(incidentLat, garantLat!);
      mapRef.current.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]],
        { padding: 60, duration: 1000 },
      );
    } catch {
      // ignore if map not ready
    }
  }, [routeInfo, hasGarant, incidentLat, incidentLng, garantLat, garantLng]);

  const routeGeoJson = useMemo(() => {
    if (!routeInfo) return null;
    return {
      type: 'Feature' as const,
      properties: {},
      geometry: routeInfo.geometry,
    };
  }, [routeInfo]);

  const formatDuration = (min: number) => {
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  return (
    <div>
      <div className="tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-200" style={{ height }}>
        <Map
          ref={mapRef}
          initialViewState={initialViewState}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          mapboxAccessToken={MAPBOX_TOKEN}
          attributionControl={false}
        >
          <NavigationControl position="top-right" />

          {/* Marker incident (rouge) */}
          <Marker longitude={incidentLng} latitude={incidentLat} anchor="bottom">
            <div className="tw-flex tw-flex-col tw-items-center">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="tw-text-red-600 tw-text-3xl tw-drop-shadow-lg" />
              <span className="tw-text-[10px] tw-font-semibold tw-text-red-700 tw-bg-white tw-px-1 tw-rounded tw-shadow tw-mt-0.5">Panne</span>
            </div>
          </Marker>

          {/* Marker garant (violet) */}
          {hasGarant && (
            <Marker longitude={garantLng!} latitude={garantLat!} anchor="bottom">
              <div className="tw-flex tw-flex-col tw-items-center">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="tw-text-affinicar-primary tw-text-3xl tw-drop-shadow-lg" />
                {garantName && (
                  <span className="tw-text-[10px] tw-font-semibold tw-text-affinicar-secondary tw-bg-white tw-px-1 tw-rounded tw-shadow tw-mt-0.5 tw-max-w-[120px] tw-truncate">
                    {garantName}
                  </span>
                )}
              </div>
            </Marker>
          )}

          {/* Route */}
          {routeGeoJson && (
            <Source type="geojson" data={routeGeoJson}>
              <Layer
                type="line"
                paint={{
                  'line-color': '#8074af',
                  'line-width': 4,
                  'line-opacity': 0.8,
                }}
              />
            </Source>
          )}
        </Map>
      </div>

      {/* Distance + durée */}
      {routeInfo && !hideDistanceLabel && (
        <p className="tw-text-sm tw-text-gray-700 tw-mt-2 tw-mb-0">
          {distanceLabelPrefix} à <span className="tw-font-medium">{routeInfo.distanceKm} km</span> ({formatDuration(routeInfo.durationMin)}) {distanceLabelSuffix}.
        </p>
      )}
    </div>
  );
}
